import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const MobileLogin = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [sessionId, setSessionId] = useState('');

  useEffect(() => {
    // Get session ID from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const session = urlParams.get('session');
    const deviceId = urlParams.get('device_id');
    if (session) {
      setSessionId(session);
    } else {
      setMessage('Invalid login session. Please scan the QR code again.');
    }
    // Stash device_id so we can include it on submit
    if (deviceId) {
      // We store temporarily on the component instance by closure
      // Alternatively, we could add state; but closure var is fine for this scope
      (window.__qr_device_id = deviceId);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!credentials.username || !credentials.password) {
      setMessage('Please enter both username and password');
      return;
    }

    if (!sessionId) {
      setMessage('Invalid session. Please scan the QR code again.');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const response = await fetch('/api/mobile-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password,
          session: sessionId,
          device_id: window.__qr_device_id || undefined
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('✅ Login successful! You can close this page.');
        setCredentials({ username: '', password: '' });
        
        // Optionally redirect or show success for a few seconds
        setTimeout(() => {
          window.close(); // Try to close the mobile browser tab
        }, 2000);
      } else {
        setMessage(`❌ ${data.error || 'Login failed'}`);
      }
    } catch (error) {
      console.error('Login error:', error);
      setMessage('❌ Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const formVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.4, delay: 0.2 }
    }
  };

  return (
    <motion.div 
      className="mobile-login"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="mobile-login-content">
        <motion.div 
          className="mobile-login-header"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <h1>🔐 Location Login</h1>
          <p>Enter your credentials to authenticate the device</p>
        </motion.div>

        <motion.form 
          onSubmit={handleSubmit}
          className="mobile-login-form"
          variants={formVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              value={credentials.username}
              onChange={(e) => setCredentials({
                ...credentials, 
                username: e.target.value
              })}
              placeholder="Enter your username"
              disabled={isLoading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={credentials.password}
              onChange={(e) => setCredentials({
                ...credentials, 
                password: e.target.value
              })}
              placeholder="Enter your password"
              disabled={isLoading}
              required
            />
          </div>

          <motion.button 
            type="submit"
            className="mobile-login-btn"
            disabled={isLoading}
            whileHover={{ scale: isLoading ? 1 : 1.05 }}
            whileTap={{ scale: isLoading ? 1 : 0.95 }}
          >
            {isLoading ? (
              <>
                <div className="spinner-small"></div>
                Authenticating...
              </>
            ) : (
              '🚀 Login'
            )}
          </motion.button>
        </motion.form>

        {message && (
          <motion.div 
            className={`mobile-message ${message.includes('✅') ? 'success' : 'error'}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {message}
          </motion.div>
        )}

        <motion.div 
          className="mobile-footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <p>🏢 DIB Customer Feedback System</p>
          <small>Session ID: {sessionId ? sessionId.substring(0, 8) + '...' : 'Invalid'}</small>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default MobileLogin;

